import { useLiveQuery } from "dexie-react-hooks";
import { Link, useSearchParams } from "react-router";
import { listDrafts } from "@/data/attachments";
import { shiftMonth } from "@/domain/accounting-period";
import { sum } from "@/domain/money";
import { formatDate, formatEurPlain, formatMonth, formatMonthTitle } from "@/lib/format";
import { SYNC_STATUS_LABELS } from "@/lib/labels";
import { Badge, EmptyState, PageHeader, PeriodSwitcher } from "@/ui/primitives";
import { db, monthParam, useCategoryTree, useExpenses, usePaymentMethods, useToday } from "../hooks";

export function ExpensesPage() {
  const { month: current } = useToday();
  const [params] = useSearchParams();
  const month = monthParam(params.get("mese") ?? undefined, current);
  const expenses = useExpenses(month);
  const tree = useCategoryTree(true);
  const methods = usePaymentMethods(false);
  const drafts = useLiveQuery(() => listDrafts(db), []);
  if (!expenses || !tree || !methods) return <p className="text-muted">Caricamento…</p>;

  const categories = new Map(tree.flatMap((g) => g.categories.map((c) => [c.id, { label: c.excelLabel, group: g.group.excelLabel }] as const)));
  const methodName = new Map(methods.map((m) => [m.id, m.name]));
  const total = sum(expenses.map((e) => e.amountEur));

  return (
    <div>
      <PageHeader
        title="Spese"
        description="Raggruppate per mese contabile: le spese con carta di credito compaiono nel mese successivo all'acquisto."
        actions={
          <Link to="/spese/nuova" className="inline-flex min-h-11 items-center rounded-lg bg-petrol px-4 font-semibold text-white">
            Nuova spesa
          </Link>
        }
      />
      {drafts && drafts.length > 0 ? (
        <section className="mb-4 rounded-xl border border-line bg-surface" aria-labelledby="drafts-title">
          <h2 id="drafts-title" className="px-4 pt-3 font-semibold">
            Bozze da completare
          </h2>
          <ul className="divide-y divide-line">
            {drafts.map(({ draft, attachments }) => (
              <li key={draft.id}>
                <Link to={`/spese/nuova?bozza=${draft.id}`} className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-ground">
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{draft.form?.merchant || (draft.origin === "camera" ? "Foto della ricevuta" : draft.origin === "library" ? "Screenshot / foto" : "Spesa")}</span>
                    <span className="block text-sm text-muted">
                      Iniziata il {formatDate(draft.createdAt.slice(0, 10))}
                      {attachments > 0 ? ", con immagine" : ""}
                    </span>
                  </span>
                  <Badge tone="warn">Bozza</Badge>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <PeriodSwitcher
        label={formatMonthTitle(month)}
        previousHref={`/spese?mese=${shiftMonth(month, -1)}`}
        nextHref={`/spese?mese=${shiftMonth(month, 1)}`}
        previousLabel={`Spese di ${formatMonth(shiftMonth(month, -1))}`}
        nextLabel={`Spese di ${formatMonth(shiftMonth(month, 1))}`}
      >
        <p className="amount text-sm text-muted">
          {expenses.length} spese, {formatEurPlain(total)}
        </p>
      </PeriodSwitcher>

      {expenses.length === 0 ? (
        <EmptyState title={`Nessuna spesa in ${formatMonth(month)}`}>Usa il pulsante + per aggiungerne una.</EmptyState>
      ) : (
        <ul className="divide-y divide-line rounded-xl border border-line bg-surface">
          {expenses.map((e) => {
            const cat = categories.get(e.categoryId);
            const deferred = e.purchaseDate.slice(0, 7) !== e.accountingMonth;
            return (
              <li key={e.id}>
                <Link to={`/spese/${e.id}`} className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-ground">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{e.merchant ?? cat?.label ?? "Categoria sconosciuta"}</p>
                    <p className="truncate text-sm text-muted">
                      {e.merchant ? `${cat?.label}, ` : ""}
                      {e.paymentMethodId ? methodName.get(e.paymentMethodId) : "da Excel"}
                    </p>
                    <p className="mt-1 flex flex-wrap gap-1">
                      {deferred ? <Badge tone="petrol">acquisto del {formatDate(e.purchaseDate)}</Badge> : null}
                      {e.source === "excel_import" ? <Badge>storico Excel</Badge> : <Badge>{SYNC_STATUS_LABELS[e.sync.status]}</Badge>}
                      {e.legacy?.formula ? <Badge>somma di più voci</Badge> : null}
                      {e.duplicate.status === "possible" ? <Badge tone="warn">Possibile duplicato</Badge> : null}
                      {e.duplicate.status === "kept" ? <Badge>Simile a un&apos;altra, tenuta</Badge> : null}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="amount font-semibold">{formatEurPlain(e.amountEur)}</p>
                    <p className="amount text-sm text-muted">{formatDate(e.purchaseDate)}</p>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
